import { useState, useCallback } from 'react';
import { format } from 'date-fns';
import { Dictionary } from 'lodash';

import { AlarmSummary, Site, TimeSeries } from 'src/models';

import { parseBitfield } from 'src/components/modules/analysis-page/helpers';

interface UseDownloadReturn {
  downloading: boolean;
  download: () => Promise<void>;
}

const escapeCSVValue = (value: string): string => {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const downloadCSV = (blob: Blob, filename: string) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};

const getFilename = (site: Site) => {
  const today = new Date();
  const formattedDate = format(today, 'MM-dd-yyyy');
  const filename = `${site.name}_Export_${formattedDate}.csv`;
  return filename;
};

const formatDateTime = (date: Date | string | number) => {
  return `${format(new Date(date), 'MM/dd/yyyy hh:mm:ss a')} ${
    Intl.DateTimeFormat().resolvedOptions().timeZone
  }`;
};

const handleBitfield = (value: number | null, snapshot: TimeSeries) => {
  if (!value) return '';
  const parsed = parseBitfield(value);
  const meanings = parsed.map((bit, index) => {
    if (bit === '1') {
      return snapshot.meanings?.filter((meaning) => meaning.value === index.toString())[0]
        ?.description;
    }
    return '';
  });

  return meanings?.filter((meaning) => meaning !== '').join(', ');
};

const parseValue = (value: number | null, snapshot: TimeSeries) => {
  if (snapshot.dataType === 'Bitfield') {
    return handleBitfield(value, snapshot);
  }
  if (snapshot.dataType === 'Enum') {
    return snapshot.meanings?.[value as number]?.description ?? value;
  }

  return value;
};

const formatAlarmData = (alarmSummaries: AlarmSummary[]): string => {
  const data: string[] = [];
  const titles = ['Alarm name', 'Error Code', 'Status', 'Trigger Time', 'Resolved Time'];

  data.push(titles.join(','));

  for (const alarm of alarmSummaries) {
    const activatedTime = alarm.activatedAt ? formatDateTime(alarm.activatedAt) : '';
    const resolvedTime = alarm.resolvedAt ? formatDateTime(alarm.resolvedAt) : '';

    const row: string[] = [
      escapeCSVValue(alarm.alarmTag.fullName),
      escapeCSVValue(alarm.alarmFaultCode || ''),
      escapeCSVValue(alarm.status),
      escapeCSVValue(activatedTime),
      escapeCSVValue(resolvedTime),
    ];
    data.push(row.join(','));
  }

  return data.join('\n');
};

const formatTimeSeriesData = (snapshots: Dictionary<TimeSeries>): string => {
  const data: string[] = [];
  const titles = ['Name', 'Description', 'Unit', 'Time', 'Value'];
  data.push(titles.join(','));

  for (const snapshot of Object.values(snapshots)) {
    for (const point of snapshot.points) {
      const value = parseValue(point.value, snapshot);
      const time = formatDateTime(point.time);

      const row: string[] = [
        escapeCSVValue(snapshot.tag.label),
        escapeCSVValue(snapshot.tag.fullName),
        escapeCSVValue(snapshot.tag.unit),
        escapeCSVValue(time),
        escapeCSVValue(value?.toString() ?? ''),
      ];
      data.push(row.join(','));
    }
  }

  return data.join('\n');
};

function useDownloadCSV<T extends AlarmSummary[] | Dictionary<TimeSeries>>(
  data: T,
  site: Site,
  dataType: 'alarms' | 'timeseries',
  timeRange?: { start?: Date; end?: Date }
): UseDownloadReturn {
  const [downloading, setDownloading] = useState(false);

  const download = useCallback(async () => {
    try {
      setDownloading(true);

      let csvContent: string;
      if (dataType === 'alarms') {
        csvContent = formatAlarmData(data as AlarmSummary[]);
      } else {
        csvContent = formatTimeSeriesData(data as Dictionary<TimeSeries>);
      }

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

      downloadCSV(blob, getFilename(site));
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloading(false);
    }
  }, [data, site, dataType, timeRange]);

  return {
    downloading,
    download,
  };
}

export const useAlarmDownloadCSV = (
  alarmSummaries: AlarmSummary[],
  site: Site
): UseDownloadReturn => {
  return useDownloadCSV(alarmSummaries, site, 'alarms');
};

export const useTimeSeriesDownloadCSV = (
  snapshots: Dictionary<TimeSeries>,
  start: Date | undefined,
  end: Date | undefined,
  site: Site
): UseDownloadReturn => {
  return useDownloadCSV(snapshots, site, 'timeseries', { start, end });
};

export default useDownloadCSV;
